import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface MessageClearConfirmationDialogProps {
  open: boolean;
  status: 'pending' | 'failed' | 'all';
  count: number;
  /** #708 — how many of `count` are recurring series; deleting one ends the whole series. */
  recurringCount?: number;
  onConfirm: () => void;
  /** #708 — clear everything except the recurring series. Offered only when recurringCount > 0. */
  onConfirmOneTimeOnly?: () => void;
  onCancel: () => void;
}

export function MessageClearConfirmationDialog({
  open,
  status,
  count,
  recurringCount = 0,
  onConfirm,
  onConfirmOneTimeOnly,
  onCancel
}: MessageClearConfirmationDialogProps) {
  const { t } = useTranslation('common');

  const getStatusLabel = () => {
    switch (status) {
      case 'pending':
        return t('messageStatusHeader.pending').toLowerCase();
      case 'failed':
        return t('messageStatusHeader.failed').toLowerCase();
      case 'all':
        return t('messageStatusHeader.total').toLowerCase();
      default:
        return status;
    }
  };

  const statusLabel = getStatusLabel();
  const hasRecurring = recurringCount > 0;
  const isAll = status === 'all';
  const messageText = isAll
    ? t('messageClearConfirmation.description_all', { count })
    : t('messageClearConfirmation.description_status', { count, status: statusLabel });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-md p-6 sm:p-8">
        <DialogHeader className="space-y-3">
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            {t('messageClearConfirmation.title')}
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            {messageText}
          </DialogDescription>
          {hasRecurring && (
            <p data-testid="clear-recurring-warning" className="text-base font-medium text-red-600">
              {t('messageClearConfirmation.recurring_warning', { count: recurringCount })}
            </p>
          )}
        </DialogHeader>
        <DialogFooter className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {hasRecurring && onConfirmOneTimeOnly && count > recurringCount && (
            <Button variant="secondary" data-testid="clear-one-time-only" onClick={onConfirmOneTimeOnly}>
              {t('messageClearConfirmation.one_time_only', { count: count - recurringCount })}
            </Button>
          )}
          <Button variant="destructive" onClick={onConfirm}>
             {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

